(function () {
  'use strict';

  var CARD_SELECTOR = '.product-grid-item:not(.carousel__item)';
  var MAX_GALLERY_IMAGES = 4;
  var productCache = new Map();
  var cardStates = new WeakMap();
  var galleryObserver;
  var MAX_ACTIVE_PRODUCT_REQUESTS = 4;
  var activeProductRequests = 0;
  var productRequestQueue = [];

  var language = (document.documentElement.lang || 'en').toLowerCase().split('-')[0];
  var translations = {
    en: {
      quickAdd: 'Quick add',
      chooseVariant: 'Choose a size',
      soldOut: 'Sold out',
      adding: 'Adding…',
      added: 'Added to cart',
      error: 'Could not add this item. Please try again.',
      previousImage: 'Previous product image',
      nextImage: 'Next product image',
      image: 'Product image',
      oneSize: 'One size'
    },
    es: {
      quickAdd: 'Añadir rápido',
      chooseVariant: 'Elige una talla',
      soldOut: 'Agotado',
      adding: 'Añadiendo…',
      added: 'Añadido al carrito',
      error: 'No se pudo añadir. Inténtalo de nuevo.',
      previousImage: 'Imagen anterior del producto',
      nextImage: 'Imagen siguiente del producto',
      image: 'Imagen del producto',
      oneSize: 'Talla única'
    }
  };
  var text = translations[language] || translations.en;

  function productDetailsFromCard(card) {
    var link = card.querySelector(
      '.product-grid-item__title[href*="/products/"], .product__media--featured[href*="/products/"], a[href*="/products/"]'
    );

    if (!link) return null;

    var url = new URL(link.getAttribute('href'), window.location.origin);
    var match = url.pathname.match(/\/products\/([^/?]+)/);
    if (!match) return null;

    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

    return {
      handle: match[1],
      jsonUrl: root + 'products/' + match[1] + '.js',
      productUrl: url.pathname + url.search
    };
  }

  function drainProductRequestQueue() {
    while (activeProductRequests < MAX_ACTIVE_PRODUCT_REQUESTS && productRequestQueue.length) {
      var job = productRequestQueue.shift();
      activeProductRequests += 1;

      Promise.resolve()
        .then(job.task)
        .then(job.resolve, job.reject)
        .then(function () {
          activeProductRequests -= 1;
          drainProductRequestQueue();
        });
    }
  }

  function queueProductRequest(task) {
    return new Promise(function (resolve, reject) {
      productRequestQueue.push({ task: task, resolve: resolve, reject: reject });
      drainProductRequestQueue();
    });
  }

  function fetchProduct(state) {
    if (state.product) return Promise.resolve(state.product);

    if (!productCache.has(state.details.jsonUrl)) {
      productCache.set(
        state.details.jsonUrl,
        queueProductRequest(function () {
          return fetch(state.details.jsonUrl, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' }
          }).then(function (response) {
            if (!response.ok) throw new Error('Product request failed: ' + response.status);
            return response.json();
          });
        }).catch(function (error) {
          productCache.delete(state.details.jsonUrl);
          throw error;
        })
      );
    }

    return productCache.get(state.details.jsonUrl).then(function (product) {
      state.product = product;
      updateAvailability(state);
      setupGallery(state);
      return product;
    });
  }

  function normaliseImage(image) {
    if (typeof image === 'string') return image;
    return image && (image.src || image.url) ? (image.src || image.url) : '';
  }

  function sizedImageUrl(source) {
    if (!source) return '';

    try {
      var url = new URL(source, window.location.origin);
      url.searchParams.set('width', window.innerWidth < 768 ? '720' : '900');
      return url.toString();
    } catch (error) {
      return source;
    }
  }

  function galleryButton(modifier, label, glyph) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'plp-card-gallery__control plp-card-gallery__control--' + modifier;
    button.setAttribute('aria-label', label);
    button.innerHTML = '<span aria-hidden="true">' + glyph + '</span>';
    return button;
  }

  function setupGallery(state) {
    if (state.galleryReady || !state.product) return;

    var mediaContainer = state.card.querySelector('.product-grid-item__image');
    var productLink = state.card.querySelector('.product__media--featured[href]');
    if (!mediaContainer || !productLink) return;

    var images = (state.product.images || [])
      .map(normaliseImage)
      .filter(Boolean)
      .filter(function (image, index, allImages) {
        return allImages.indexOf(image) === index;
      })
      .slice(0, MAX_GALLERY_IMAGES);

    if (images.length < 2) {
      state.galleryReady = true;
      return;
    }

    state.galleryReady = true;
    state.galleryImages = images;
    state.galleryIndex = 0;

    var imageLink = document.createElement('a');
    imageLink.className = 'plp-card-gallery__link';
    imageLink.href = state.details.productUrl;
    imageLink.setAttribute('aria-label', state.product.title || productLink.getAttribute('aria-label') || '');

    var image = document.createElement('img');
    image.className = 'plp-card-gallery__image';
    image.loading = 'lazy';
    image.decoding = 'async';
    image.alt = state.product.title || '';
    imageLink.appendChild(image);

    var previous = galleryButton('previous', text.previousImage, '&#8249;');
    var next = galleryButton('next', text.nextImage, '&#8250;');
    var dots = document.createElement('div');
    dots.className = 'plp-card-gallery__dots';
    dots.setAttribute('aria-label', text.image);

    images.forEach(function (_image, index) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'plp-card-gallery__dot' + (index === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', text.image + ' ' + (index + 1));
      dot.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        showGalleryImage(state, index);
      });
      dots.appendChild(dot);
    });

    previous.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      showGalleryImage(state, state.galleryIndex - 1);
    });

    next.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      showGalleryImage(state, state.galleryIndex + 1);
    });

    var pointerStart;
    mediaContainer.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse') return;
      pointerStart = { x: event.clientX, y: event.clientY };
    });

    mediaContainer.addEventListener('pointerup', function (event) {
      if (!pointerStart || event.pointerType === 'mouse') return;

      var deltaX = event.clientX - pointerStart.x;
      var deltaY = event.clientY - pointerStart.y;
      pointerStart = null;

      if (Math.abs(deltaX) < 35 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

      event.preventDefault();
      state.preventNextMediaClick = true;
      showGalleryImage(state, state.galleryIndex + (deltaX < 0 ? 1 : -1));
    });

    mediaContainer.addEventListener('click', function (event) {
      if (!state.preventNextMediaClick) return;
      state.preventNextMediaClick = false;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    state.galleryElements = {
      imageLink: imageLink,
      image: image,
      dots: dots
    };

    mediaContainer.appendChild(imageLink);
    mediaContainer.appendChild(previous);
    mediaContainer.appendChild(next);
    mediaContainer.appendChild(dots);
  }

  function showGalleryImage(state, requestedIndex) {
    if (!state.galleryElements || !state.galleryImages.length) return;

    var count = state.galleryImages.length;
    var index = (requestedIndex % count + count) % count;
    var elements = state.galleryElements;

    state.galleryIndex = index;

    if (index === 0) {
      elements.imageLink.classList.remove('is-visible');
      elements.imageLink.setAttribute('aria-hidden', 'true');
    } else {
      var source = sizedImageUrl(state.galleryImages[index]);
      if (elements.image.getAttribute('src') !== source) elements.image.src = source;
      elements.imageLink.classList.add('is-visible');
      elements.imageLink.removeAttribute('aria-hidden');
    }

    Array.prototype.forEach.call(elements.dots.children, function (dot, dotIndex) {
      dot.classList.toggle('is-active', dotIndex === index);
      dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
    });
  }

  function buildQuickAdd(state) {
    var info = state.card.querySelector('.product-grid-item__info');
    var mediaContainer = state.card.querySelector('.product-grid-item__image');
    if (!info || !mediaContainer) return;

    var wrapper = document.createElement('div');
    wrapper.className = 'plp-quick-add';

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'plp-quick-add__button';
    button.textContent = text.quickAdd;
    button.setAttribute('aria-expanded', 'false');

    var panel = document.createElement('div');
    panel.className = 'plp-quick-add__panel';
    panel.hidden = true;

    var heading = document.createElement('p');
    heading.className = 'plp-quick-add__heading';
    heading.textContent = text.chooseVariant;

    var variants = document.createElement('div');
    variants.className = 'plp-quick-add__variants';

    var status = document.createElement('p');
    status.className = 'plp-quick-add__status';
    status.setAttribute('aria-live', 'polite');

    panel.appendChild(heading);
    panel.appendChild(variants);
    panel.appendChild(status);
    wrapper.appendChild(button);
    wrapper.appendChild(panel);
    mediaContainer.appendChild(wrapper);

    state.quickAdd = {
      wrapper: wrapper,
      button: button,
      panel: panel,
      variants: variants,
      status: status,
      rendered: false
    };

    button.addEventListener('click', function () {
      if (button.disabled) return;

      setQuickAddBusy(state, true, text.adding);

      fetchProduct(state).then(function (product) {
        var availableVariants = product.variants.filter(function (variant) {
          return variant.available;
        });

        if (!availableVariants.length) {
          updateAvailability(state);
          return;
        }

        renderVariants(state);
        toggleVariantPanel(state);
      }).catch(function () {
        showStatus(state, text.error, 'error');
      }).finally(function () {
        setQuickAddBusy(state, false);
      });
    });

    panel.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      closeVariantPanel(state);
      button.focus();
    });
  }

  function updateAvailability(state) {
    if (!state.product || !state.quickAdd) return;

    var available = state.product.variants.some(function (variant) {
      return variant.available;
    });

    state.quickAdd.button.disabled = !available;
    state.quickAdd.button.textContent = available ? text.quickAdd : text.soldOut;
  }

  function variantLabel(variant) {
    var label = variant.public_title || variant.title || '';
    return !label || label === 'Default Title' ? text.oneSize : label;
  }

  function renderVariants(state) {
    if (state.quickAdd.rendered) return;

    state.quickAdd.rendered = true;
    state.product.variants.forEach(function (variant) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'plp-quick-add__variant';
      button.textContent = variantLabel(variant);
      button.disabled = !variant.available;
      button.setAttribute('aria-label', variantLabel(variant) + (variant.available ? '' : ' — ' + text.soldOut));
      button.addEventListener('click', function () {
        addVariantToCart(state, variant.id);
      });
      state.quickAdd.variants.appendChild(button);
    });
  }

  function toggleVariantPanel(state) {
    var opening = state.quickAdd.panel.hidden;
    state.quickAdd.panel.hidden = !opening;
    state.quickAdd.button.setAttribute('aria-expanded', opening ? 'true' : 'false');
    if (opening) {
      var firstAvailable = state.quickAdd.variants.querySelector('button:not(:disabled)');
      if (firstAvailable) firstAvailable.focus();
    }
  }

  function closeVariantPanel(state) {
    state.quickAdd.panel.hidden = true;
    state.quickAdd.button.setAttribute('aria-expanded', 'false');
  }

  function setQuickAddBusy(state, busy, label) {
    if (!state.quickAdd) return;
    state.quickAdd.button.disabled = busy;
    state.quickAdd.button.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (busy && label) state.quickAdd.button.textContent = label;
    if (!busy) updateAvailability(state);

    Array.prototype.forEach.call(state.quickAdd.variants.children, function (button) {
      var variant = state.product && state.product.variants
        ? state.product.variants[Array.prototype.indexOf.call(state.quickAdd.variants.children, button)]
        : null;
      button.disabled = busy || (variant && !variant.available);
    });
  }

  function showStatus(state, message, type) {
    if (!state.quickAdd) return;
    state.quickAdd.status.textContent = message;
    state.quickAdd.status.classList.toggle('is-error', type === 'error');
    state.quickAdd.status.classList.toggle('is-success', type === 'success');
  }

  function addVariantToCart(state, variantId) {
    setQuickAddBusy(state, true, text.adding);
    showStatus(state, '', '');

    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

    return fetch(root + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }]
      })
    }).then(function (response) {
      return response.json().then(function (payload) {
        if (!response.ok) throw new Error(payload.description || text.error);
        return payload;
      });
    }).then(function (payload) {
      closeVariantPanel(state);
      showStatus(state, text.added, 'success');
      state.quickAdd.button.textContent = text.added;
      refreshCart(root, payload);

      window.setTimeout(function () {
        updateAvailability(state);
      }, 1800);
    }).catch(function (error) {
      showStatus(state, error.message || text.error, 'error');
    }).finally(function () {
      setQuickAddBusy(state, false);
    });
  }

  function refreshCart(root, addPayload) {
    fetch(root + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('Cart request failed');
      return response.json();
    }).then(function (cart) {
      var selectors = [
        '[data-cart-count]',
        '.cart__count',
        '.header__cart__count',
        '.cart-count',
        '.js-cart-count'
      ];

      document.querySelectorAll(selectors.join(',')).forEach(function (counter) {
        counter.textContent = cart.item_count;
      });

      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: cart, source: 'plp-quick-add', item: addPayload }
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart: cart }
      }));
    }).catch(function () {
      // The add succeeded; a cart app can still refresh itself from the add request.
    });

    if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
      window.ajaxCart.load();
    }
  }

  function enhanceCard(card) {
    if (card.dataset.plpCardEnhanced === 'true') return;

    var details = productDetailsFromCard(card);
    if (!details) return;

    card.dataset.plpCardEnhanced = 'true';
    card.classList.add('plp-card-enhanced');

    var state = {
      card: card,
      details: details,
      product: null,
      galleryReady: false
    };

    cardStates.set(card, state);
    buildQuickAdd(state);
    galleryObserver.observe(card);
  }

  function enhanceCards(root) {
    var scope = root && root.querySelectorAll ? root : document;
    if (scope.matches && scope.matches(CARD_SELECTOR)) enhanceCard(scope);
    scope.querySelectorAll(CARD_SELECTOR).forEach(enhanceCard);
  }

  function initialise() {
    galleryObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        galleryObserver.unobserve(entry.target);

        var state = cardStates.get(entry.target);
        if (!state) return;
        fetchProduct(state).catch(function () {
          // Quick Add retries the request on interaction and reports any error there.
        });
      });
    }, { rootMargin: '300px 0px' });

    enhanceCards(document);

    var mutationObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) enhanceCards(node);
        });
      });
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    ['globoFilterRenderSearchCompleted', 'globoFilterRenderCollectionCompleted'].forEach(function (eventName) {
      window.addEventListener(eventName, function () {
        enhanceCards(document);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise);
  } else {
    initialise();
  }
})();
